/**
 * Variant Options Section JavaScript
 * Handles add to cart functionality and quantity management
 */

class VariantOptions {
  constructor() {
    this.init();
  }

  init() {
    this.bindEvents();
    this.initFilters();
    // Re-init when a section is (re)loaded in the Theme Editor
    document.addEventListener('shopify:section:load', () => {
      this.initFilters();
      this.filterVariants();
      this.initViewAll();
      this.restoreExpandedState();
    });
    this.initViewAll();
    this.restoreExpandedState();
  }

  bindEvents() {
    // Form submissions
    document.addEventListener('submit', (e) => {
      if (e.target.classList.contains('variant-option__form')) {
        e.preventDefault();
        this.handleFormSubmit(e.target);
      }
    });

    // Quantity input changes
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('qty-input__input')) {
        this.handleQuantityChange(e.target);
      }
    });

    // Quantity input validation
    document.addEventListener('input', (e) => {
      if (e.target.classList.contains('qty-input__input')) {
        this.validateQuantity(e.target);
      }
    });

    // Quantity plus/minus buttons - let theme handle this
  }

  initFilters() {
    // Initialize custom dropdowns
    this.initCustomSelects();
    
    // Delegate change events so it works for dynamically inserted sections
    if (!this._filtersBound) {
      document.addEventListener('click', (e) => {
        if (e.target.closest('.custom-select')) {
          this.handleCustomSelectClick(e);
        }
        if (e.target.classList.contains('custom-select__option')) {
          this.handleOptionSelect(e);
        }
        if (e.target.classList.contains('clear-filters-btn')) {
          this.handleClearFilters(e);
        }
      });
      
      // Close dropdowns when clicking outside
      document.addEventListener('click', (e) => {
        if (!e.target.closest('.custom-select')) {
          this.closeAllDropdowns();
        }
      });
      
      this._filtersBound = true;
    }
  }

  initCustomSelects() {
    const customSelects = document.querySelectorAll('.custom-select');
    customSelects.forEach((select, index) => {
      const trigger = select.querySelector('.custom-select__trigger');
      const options = select.querySelectorAll('.custom-select__option');
      const valueSpan = select.querySelector('.custom-select__value');
      
      // Set initial state
      const firstOption = options[0];
      if (firstOption && valueSpan) {
        firstOption.classList.add('selected');
        valueSpan.textContent = firstOption.textContent;
      }
    });
  }

  handleCustomSelectClick(e) {
    e.preventDefault();
    e.stopPropagation();
    
    const select = e.target.closest('.custom-select');
    if (!select) return;
    
    const trigger = select.querySelector('.custom-select__trigger');
    const options = select.querySelector('.custom-select__options');
    
    if (!trigger || !options) return;
    
    // Close other dropdowns
    this.closeAllDropdowns();
    
    // Toggle current dropdown
    trigger.classList.toggle('active');
    options.classList.toggle('active');
  }

  handleOptionSelect(e) {
    e.stopPropagation();
    const option = e.target;
    const select = option.closest('.custom-select');
    const trigger = select.querySelector('.custom-select__trigger');
    const options = select.querySelector('.custom-select__options');
    const valueSpan = select.querySelector('.custom-select__value');
    
    // Update selected option
    select.querySelectorAll('.custom-select__option').forEach(opt => {
      opt.classList.remove('selected');
    });
    option.classList.add('selected');
    
    // Update display value
    valueSpan.textContent = option.textContent;
    
    // Close dropdown
    trigger.classList.remove('active');
    options.classList.remove('active');
    
    // Trigger filter
    this.filterVariants();
  }

  closeAllDropdowns() {
    document.querySelectorAll('.custom-select').forEach(select => {
      const trigger = select.querySelector('.custom-select__trigger');
      const options = select.querySelector('.custom-select__options');
      if (trigger) trigger.classList.remove('active');
      if (options) options.classList.remove('active');
    });
  }

  handleClearFilters(e) {
    e.preventDefault();
    e.stopPropagation();
    
    // Reset all dropdowns to "All"
    const customSelects = document.querySelectorAll('.custom-select');
    customSelects.forEach(select => {
      const firstOption = select.querySelector('.custom-select__option[data-value="__all__"]');
      if (firstOption) {
        // Remove selected class from all options
        select.querySelectorAll('.custom-select__option').forEach(opt => {
          opt.classList.remove('selected');
        });
        
        // Select "All" option
        firstOption.classList.add('selected');
        
        // Update display value
        const valueSpan = select.querySelector('.custom-select__value');
        if (valueSpan) {
          valueSpan.textContent = firstOption.textContent;
        }
      }
    });
    
    // Trigger filter to show all variants
    this.filterVariants();
  }

  filterVariants() {
    const customSelects = document.querySelectorAll('.custom-select');
    if (!customSelects.length) return;

    const variantList = document.querySelector('.variant-options__list');
    
    // Add loading state
    if (variantList) {
      variantList.style.opacity = '0.6';
      variantList.style.transition = 'opacity 0.2s ease';
    }

    const selected = {};
    customSelects.forEach((select) => {
      const pos = select.dataset.optionPosition;
      const selectedOption = select.querySelector('.custom-select__option.selected');
      const val = selectedOption ? selectedOption.dataset.value : '';
      if (val && val !== '__all__') selected[pos] = val.toLowerCase();
    });

    const cards = document.querySelectorAll('.variant-option');
    let visibleCount = 0;
    
    cards.forEach((card, index) => {
      let visible = true;
      Object.keys(selected).forEach((pos) => {
        const cardVal = (card.getAttribute(`data-opt-${pos}`) || '').toLowerCase();
        if (cardVal !== selected[pos]) visible = false;
      });
      
      if (visible) {
        visibleCount++;
        card.style.display = '';
        card.style.opacity = '0';
        card.style.transform = 'translateY(10px)';
        card.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
        
        // Remove hidden class when filtering (show all matching variants)
        card.classList.remove('variant-option--hidden');
        
        // Staggered animation
        setTimeout(() => {
          card.style.opacity = '1';
          card.style.transform = 'translateY(0)';
        }, index * 30);
      } else {
        card.style.display = 'none';
        card.classList.add('variant-option--hidden');
      }
    });
    
    // Remove loading state
    setTimeout(() => {
      if (variantList) {
        variantList.style.opacity = '1';
      }
      
      // Update view all button visibility based on visible count
      this.updateViewAllButton(visibleCount, cards.length);
      
      // Show filter result message
      this.showFilterResults(visibleCount, cards.length);
    }, 200);
  }

  showFilterResults(visibleCount, totalCount) {
    // Remove any existing result message
    const existingMessage = document.querySelector('.variant-filter-results');
    if (existingMessage) {
      existingMessage.remove();
    }
    
    // Show/hide clear button based on filtering state
    const clearButton = document.querySelector('.variant-options__clear-filters');
    if (clearButton) {
      clearButton.style.display = visibleCount !== totalCount ? 'flex' : 'none';
    }
    
    if (visibleCount !== totalCount) {
      // Create result message
      const message = document.createElement('div');
      message.className = 'variant-filter-results';
      message.style.cssText = `
        text-align: center;
        margin: 1rem 0;
        padding: 0.75rem 1rem;
        background: linear-gradient(45deg, #00b0f0, #ff62b1, #ffe700, #00b0f0);
        background-size: 300% 300%;
        animation: gradientShift 15s ease-in-out infinite;
        border-radius: 8px;
        color: white;
        font-size: 14px;
        font-weight: 600;
        text-shadow: 0 1px 2px rgba(0,0,0,0.3);
        box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        opacity: 0;
        transform: translateY(-10px);
        transition: opacity 0.8s ease, transform 0.8s ease;
      `;
      
      // Add CSS animation keyframes
      if (!document.querySelector('#gradient-animation-styles')) {
        const style = document.createElement('style');
        style.id = 'gradient-animation-styles';
        style.textContent = `
          @keyframes gradientShift {
            0% { background-position: 0% 50%; }
            25% { background-position: 25% 50%; }
            50% { background-position: 50% 50%; }
            75% { background-position: 75% 50%; }
            100% { background-position: 100% 50%; }
          }
        `;
        document.head.appendChild(style);
      }
      
      message.textContent = `Showing ${visibleCount} of ${totalCount} variants`;
      
      // Insert after filters
      const filters = document.querySelector('.variant-options__filters');
      if (filters) {
        filters.parentNode.insertBefore(message, filters.nextSibling);
        
        // Animate in
        setTimeout(() => {
          message.style.opacity = '1';
          message.style.transform = 'translateY(0)';
        }, 100);
      }
    }
  }

  updateViewAllButton(visibleCount, totalCount) {
    const viewAllBtn = document.querySelector('.variant-options__view-all-btn');
    const variantList = document.querySelector('.variant-options__list');
    
    if (!viewAllBtn || !variantList) return;
    
    // If filtering is active and showing fewer than 5 variants, hide the view all button
    const isFiltering = visibleCount < totalCount;
    const shouldShowViewAll = !isFiltering && totalCount > 5;
    
    if (shouldShowViewAll) {
      viewAllBtn.style.display = 'block';
      variantList.classList.add('variant-options__list--collapsed');
    } else {
      viewAllBtn.style.display = 'none';
      variantList.classList.remove('variant-options__list--collapsed');
    }
  }

  initViewAll() {
    const list = document.querySelector('.variant-options__list');
    const btn = document.querySelector('.js-view-all-variants');
    const fade = document.querySelector('.variant-options__fade');
    if (!list || !btn) return;

    const hasMany = list.children.length > 5;
    if (!hasMany) {
      if (btn) btn.style.display = 'none';
      if (fade) fade.style.display = 'none';
      return;
    }

    const expand = () => {
      list.setAttribute('data-collapsed', 'false');
      if (btn) btn.style.display = 'none';
      if (fade) fade.style.display = 'none';
    };

    btn.addEventListener('click', expand);
  }

  handleFormSubmit(form) {
    const formData = new FormData(form);
    const submitButton = form.querySelector('button[type="submit"]');

    // Disable button during processing
    submitButton.disabled = true;
    submitButton.textContent = 'Adding...';

    fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.status === 422) {
        throw new Error(data.description || 'Unable to add item to cart');
      }
      
      // Success - show message and update cart
      this.showSuccess('Item added to cart successfully');
      this.triggerCartUpdate();
      
      // Reset button
      submitButton.disabled = false;
      submitButton.textContent = 'Add to cart';
    })
    .catch(error => {
      console.error('Add to cart error:', error);
      this.showError(error.message || 'Unable to add item to cart');
      
      // Reset button
      submitButton.disabled = false;
      submitButton.textContent = 'Add to cart';
    });
  }



  handleAddToQuote(button) {
    const variantId = button.dataset.variantId;
    const quantityInput = document.getElementById(`quantity-${variantId}`);
    const quantity = parseInt(quantityInput.value) || 1;

    if (quantity < 1) {
      this.showError('Please enter a valid quantity');
      return;
    }

    // For now, just show a message - you can implement quote functionality later
    this.showInfo('Quote functionality coming soon');
  }

  handleQuantityChange(input) {
    const quantity = parseInt(input.value);
    if (quantity < 1) {
      input.value = 1;
    }
  }

  validateQuantity(input) {
    const value = input.value;
    const quantity = parseInt(value);
    
    if (isNaN(quantity) || quantity < 1) {
      input.value = 1;
    }
  }


  triggerCartUpdate() {
    // Use the theme's proper cart refresh event
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
      bubbles: true
    }));
    
    // Open cart drawer using theme's method
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
  }

  showSuccess(message) {
    this.showNotification(message, 'success');
  }

  showError(message) {
    this.showNotification(message, 'error');
  }

  showInfo(message) {
    this.showNotification(message, 'info');
  }

  initViewAll() {
    // Handle "View all" and "Collapse" button clicks
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('variant-options__view-all-btn')) {
        e.preventDefault();
        this.handleViewAll(e.target);
      } else if (e.target.classList.contains('variant-options__collapse-btn')) {
        e.preventDefault();
        this.handleCollapse(e.target);
      }
    });
  }

  restoreExpandedState() {
    // Check all variant options sections for expanded state
    const sections = document.querySelectorAll('[id^="variant-options-"]');
    
    sections.forEach(section => {
      const sectionId = section.id.replace('variant-options-', '');
      const isExpanded = sessionStorage.getItem(`variant-options-expanded-${sectionId}`);
      
      if (isExpanded === 'true') {
        const variantList = document.getElementById(`variant-list-${sectionId}`);
        const viewAllButton = section.querySelector('.variant-options__view-all-btn');
        
        if (variantList && viewAllButton) {
          // Restore expanded state
          variantList.classList.remove('variant-options__list--collapsed');
          variantList.classList.add('variant-options__list--expanded');
          
          // Change button to collapse button
          viewAllButton.textContent = 'Collapse';
          viewAllButton.classList.remove('variant-options__view-all-btn');
          viewAllButton.classList.add('variant-options__collapse-btn');
          
          // Show all hidden variants
          const hiddenVariants = variantList.querySelectorAll('.variant-option--hidden');
          hiddenVariants.forEach(variant => {
            variant.style.display = 'block';
          });
        }
      }
    });
  }

  handleCollapse(button) {
    const sectionId = button.getAttribute('data-section-id');
    const variantList = document.getElementById(`variant-list-${sectionId}`);
    
    if (variantList) {
      // Add collapsed class and remove expanded class
      variantList.classList.add('variant-options__list--collapsed');
      variantList.classList.remove('variant-options__list--expanded');
      
      // Change button back to view all button
      button.textContent = `View All ${variantList.querySelectorAll('.variant-option').length} Options`;
      button.classList.remove('variant-options__collapse-btn');
      button.classList.add('variant-options__view-all-btn');
      
      // Hide variants beyond the first 5
      const allVariants = variantList.querySelectorAll('.variant-option');
      allVariants.forEach((variant, index) => {
        if (index >= 5) {
          variant.classList.add('variant-option--hidden');
          variant.style.display = 'none';
        }
      });
      
      // Remove expanded state from sessionStorage
      sessionStorage.removeItem(`variant-options-expanded-${sectionId}`);
    }
  }

  handleViewAll(button) {
    const sectionId = button.getAttribute('data-section-id');
    const variantList = document.getElementById(`variant-list-${sectionId}`);
    
    if (variantList) {
      // Remove collapsed class and add expanded class
      variantList.classList.remove('variant-options__list--collapsed');
      variantList.classList.add('variant-options__list--expanded');
      
      // Change button to collapse button
      button.textContent = 'Collapse';
      button.classList.remove('variant-options__view-all-btn');
      button.classList.add('variant-options__collapse-btn');
      
      // Show all hidden variants
      const hiddenVariants = variantList.querySelectorAll('.variant-option--hidden');
      hiddenVariants.forEach(variant => {
        variant.style.display = 'block';
      });
      
      // Store expanded state in sessionStorage for this page
      sessionStorage.setItem(`variant-options-expanded-${sectionId}`, 'true');
    }
  }

  showNotification(message, type = 'info') {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `variant-options__notification variant-options__notification--${type}`;
    notification.textContent = message;

    // Add styles
    Object.assign(notification.style, {
      position: 'fixed',
      top: '20px',
      right: '20px',
      padding: '12px 20px',
      borderRadius: '4px',
      color: 'white',
      fontWeight: '500',
      zIndex: '9999',
      transform: 'translateX(100%)',
      transition: 'transform 0.3s ease'
    });

    // Set background color based on type
    const colors = {
      success: '#10b981',
      error: '#ef4444',
      info: '#3b82f6'
    };
    notification.style.backgroundColor = colors[type] || colors.info;

    // Add to DOM
    document.body.appendChild(notification);

    // Animate in
    requestAnimationFrame(() => {
      notification.style.transform = 'translateX(0)';
    });

    // Remove after delay
    setTimeout(() => {
      notification.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 3000);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new VariantOptions();
  });
} else {
  new VariantOptions();
}

// Export for potential use in other scripts
window.VariantOptions = VariantOptions;
